import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { IconButton, Button } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';

import Layout from '../../../components/Layout';
import Select from '../../../components/Select';
import Table from '../../../components/Table';
import Dialog from '../../../components/Dialog';

import useSnackbarText from '../../../hooks/useSnackbarText';
import useQueryParams from '../../../hooks/useQueryParams';

import contests from '../../../api/contests';
import { 
	getSitemapData, 
	getYearList,
	getObjectFromArray,
} from '../../../utils';
import { 
	contestTypeOptions,
} from '../../../configs/contests';

const emptyFunc = () => {};

const dateFormat = 'YYYY-MM-DD';
const sitemapValue = 'ADMIN_CONTEST';
const { fullPathname, label } = getSitemapData(sitemapValue);

const headers = [
	{ 
		id: 'name',
		label: 'Name', 
		type: 'string',
		sticky: true,
	},
	{
		id: 'startDate',
		label: 'Start Date',
		type: 'date',
	},
	{ 
		id: 'endDate',
		label: 'End Date', 
		type: 'date',
	},
	{ 
		id: 'NOCTarget',
		label: 'Minimum ANP', 
		type: 'float',
	},
	{ 
		id: 'type',
		label: 'Type', 
		type: 'string',
	},
	{ 
		id: 'remark',
		label: 'Remark', 
		type: 'string',
	},
	{ 
		id: 'edit',
		label: 'Edit', 
		type: 'dom',
	},
	{
		id: 'setHighlight',
		label: 'Set Active',
		type: 'dom'
	},
	{
		id: 'remove',
		label: 'Delete',
		type: 'dom'
	},
];

const useStyles = makeStyles(theme => ({
	dialogContentWrapper: {
		display: 'flex',
		justifyContent: 'space-evenly',
		marginTop: 10,
		marginBottom: 10,
	}
}));

function Main() {
	const classes = useStyles();
	const handleSnackbarChange = useSnackbarText();
	const [queryParams, setQueryParams] = useQueryParams();
	const [data, setData] = useState([]);
	const [timestamp, setTimestamp] = useState(moment().unix())
	const [year, setYear] = useState(
		(queryParams.startDate && moment(queryParams.startDate).year())
		|| moment().year()
	);

	const handleYearChange = (event) => {
		setYear(event.target.value);
	};

	const handleDelete = useCallback((contestID) => async () => {
		let { data, success } = await contests.deleteContest({ contestID });
		if(!success) {
			return handleSnackbarChange({ text: data.message, variant: 'error' });
		} else {
			handleSnackbarChange({ text: 'Contest deleted', variant: 'success' });
			setTimestamp(moment().unix());
		}
		setQueryParams({ deleteID: null });
	}, [handleSnackbarChange, setQueryParams]);

	const handleSetDeleteID = useCallback((contestID) => () => {
		setQueryParams({ deleteID: contestID });
	}, [setQueryParams]);

	const handleSetHighlight = useCallback((contestID) => async () => {
		let { data, success } = await contests.highlightContest({ contestID });
		if(!success) {
			return handleSnackbarChange({ text: data.message, variant: 'error' });
		} else {
			handleSnackbarChange({ text: 'Contest updated', variant: 'success' });
			setTimestamp(moment().unix());
		}
	}, [handleSnackbarChange]);

	useEffect(() => {
		let startDate, endDate;
		startDate = moment(`${year}-01-01`).format(dateFormat);
		endDate = moment(`${year}-12-31`).format(dateFormat);
		setQueryParams({ startDate, endDate });
	}, [year, setQueryParams]);

	useEffect(() => {
		const getData = async () => {
			const { success, data } = await contests.getContests({ 
				startDate: queryParams.startDate,
				endDate: queryParams.endDate,
			});
			if(!success) {
				return handleSnackbarChange({ text: data.message, variant: 'error' });
			} else {
				setData(data.contests.map(e => ({
					...e,
					type: getObjectFromArray(contestTypeOptions, e.type).label,
					edit: 
						<IconButton
							component={Link}
							to={`${fullPathname}/update?contestID=${e.contestID}`}
						>
							<EditIcon />
						</IconButton>,
					setHighlight: 
						<IconButton
							onClick={
								e.isHighlighted ? emptyFunc : handleSetHighlight(e.contestID)
							}
							color="secondary"
						>
							{e.isHighlighted ? <StarIcon /> : <StarBorderIcon />}
						</IconButton>,
					remove: 
						<IconButton  onClick={handleSetDeleteID(e.contestID)}>
							<DeleteIcon />
						</IconButton>,
				})));
			}
		}
		if(queryParams.startDate && queryParams.endDate) getData();
	}, [queryParams.startDate, queryParams.endDate, handleSnackbarChange, timestamp, handleSetDeleteID, handleSetHighlight]);

	return (
		<Layout title={label} createPathname={fullPathname + '/create'}>
			<Select 
				className={classes.select}
				maxWidth={150}
				label="Year"
				options={getYearList()}
				value={String(year)}
				onChange={handleYearChange}
			/>

			<Table
				headers={headers}
				data={data}
			/>

			<Dialog title="Delete this contest?" queryParamKey="deleteID">
				<div className={classes.dialogContentWrapper}>
					<Button variant="contained" color="primary" onClick={handleDelete(queryParams.deleteID)}>Delete</Button>
					<Button onClick={() => setQueryParams({ deleteID: null })}>Cancel</Button>
				</div>
			</Dialog>
		</Layout>
	);
}

export default Main;
